'use client'

import { useState, useEffect } from 'react'
import { getPersonalization, EMPHASIS_MESSAGES, type UTMPersonalization } from '@/lib/utm-personalization'

interface UTMData {
  source: string | null
  medium: string | null
  campaign: string | null
  content: string | null
  term: string | null
  id: string | null
  gclid: string | null
}

interface UseUTMPersonalizationResult {
  personalization: UTMPersonalization
  utm: UTMData | null
  emphasis: typeof EMPHASIS_MESSAGES[keyof typeof EMPHASIS_MESSAGES] | null
  isFromAds: boolean
  isRemarketing: boolean
}

const DEFAULT_UTM: UTMData = {
  source: null,
  medium: null,
  campaign: null,
  content: null,
  term: null,
  id: null,
  gclid: null
}

export function useUTMPersonalization(): UseUTMPersonalizationResult {
  const [utm, setUtm] = useState<UTMData | null>(null)
  const [personalization, setPersonalization] = useState<UTMPersonalization>({})

  useEffect(() => {
    // Buscar UTMs do sessionStorage (salvos pelo analytics-collector)
    const utmData = sessionStorage.getItem('biomo_utm_params')

    if (utmData) {
      try {
        const parsed = JSON.parse(utmData) as UTMData
        setUtm(parsed)

        // Aplicar personalização
        const personal = getPersonalization({
          source: parsed.source,
          campaign: parsed.campaign,
          term: parsed.term
        })
        setPersonalization(personal)
      } catch {
        setUtm(DEFAULT_UTM)
      }
    } else {
      // Se não tem no sessionStorage, tentar extrair da URL atual
      const params = new URLSearchParams(window.location.search)
      const utmFromUrl: UTMData = {
        source: params.get('utm_source'),
        medium: params.get('utm_medium'),
        campaign: params.get('utm_campaign'),
        content: params.get('utm_content'),
        term: params.get('utm_term'),
        id: params.get('utm_id'),
        gclid: params.get('gclid')
      }

      setUtm(utmFromUrl)

      const personal = getPersonalization({
        source: utmFromUrl.source,
        campaign: utmFromUrl.campaign,
        term: utmFromUrl.term
      })
      setPersonalization(personal)
    }
  }, [])

  // Verificar se é tráfego pago
  const isFromAds = !!(
    utm?.gclid ||
    utm?.medium === 'cpc' ||
    utm?.medium === 'paid' ||
    utm?.source === 'google' ||
    utm?.source === 'facebook' ||
    utm?.source === 'instagram'
  )

  // Verificar se é remarketing
  const isRemarketing = !!(
    utm?.campaign?.toLowerCase().includes('remarketing') ||
    utm?.campaign?.toLowerCase().includes('retargeting') ||
    utm?.campaign?.toLowerCase().includes('rmk')
  )

  // Obter mensagem de ênfase
  const emphasis = personalization.emphasis
    ? EMPHASIS_MESSAGES[personalization.emphasis]
    : null

  return {
    personalization,
    utm,
    emphasis,
    isFromAds,
    isRemarketing
  }
}
